/**
 * PipelineMap ,  Google Maps view of every opportunity in pipeline + watchlist.
 *
 * Pins are coloured by cf_stage so operators can see at a glance which
 * markets have engaged/offer/LOI deals stacking up. Click a pin to open an
 * InfoWindow with title, score, stage, and a deep-link to the detail page.
 */
import { useMemo, useState } from "react";
import { APIProvider, Map, AdvancedMarker, InfoWindow } from "@vis.gl/react-google-maps";
import { Link } from "react-router-dom";
import { MapPin, AlertTriangle, ArrowUpRight } from "lucide-react";
import { GMAPS_KEY, GMAPS_MAP_ID, GMAPS_LIBS, isMapsKeyConfigured } from "./gmaps";
import { fmtCurrencyFull } from "@/lib/format";

// Stage palette (hex) ,  keeps map readable against dark base.
const STAGE_COLORS = {
  discovered:   { bg: "#64748B", border: "#94A3B8", glyph: "#0E1116", label: "Discovered" },
  scored:       { bg: "#38BDF8", border: "#7DD3FC", glyph: "#0E1116", label: "Scored" },
  watchlist:    { bg: "#475569", border: "#64748B", glyph: "#F1F5F9", label: "Discovery Queue" },
  engaged:      { bg: "#F59E0B", border: "#FBBF24", glyph: "#0E1116", label: "Engaged" },
  offer:        { bg: "#F97316", border: "#FB923C", glyph: "#FFFFFF", label: "Offer" },
  under_loi:    { bg: "#EAB308", border: "#FACC15", glyph: "#0E1116", label: "Under LOI" },
  due_diligence:{ bg: "#A855F7", border: "#C084FC", glyph: "#FFFFFF", label: "Due Diligence" },
  signed:       { bg: "#10B981", border: "#34D399", glyph: "#0E1116", label: "Signed" },
  closed:       { bg: "#059669", border: "#10B981", glyph: "#FFFFFF", label: "Closed" },
  dead:         { bg: "#1F2937", border: "#374151", glyph: "#94A3B8", label: "Dead" },
};

const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 }; // CONUS center
const DEFAULT_ZOOM = 4;

export default function PipelineMap({ points = [], missing = 0 }) {
  const [active, setActive] = useState(null);
  const [stageFilter, setStageFilter] = useState("all");

  // Stage counts for legend / filter chips
  const counts = useMemo(() => {
    const c = {};
    for (const p of points) {
      const s = p.cf_stage || "discovered";
      c[s] = (c[s] || 0) + 1;
    }
    return c;
  }, [points]);

  const visible = useMemo(() => (
    points.filter((p) =>
      Number.isFinite(p.lat) && Number.isFinite(p.lng)
      && (stageFilter === "all" || (p.cf_stage || "discovered") === stageFilter),
    )
  ), [points, stageFilter]);

  // Compute a sensible center if all points cluster
  const center = useMemo(() => {
    if (visible.length === 0) return DEFAULT_CENTER;
    const lat = visible.reduce((a, p) => a + p.lat, 0) / visible.length;
    const lng = visible.reduce((a, p) => a + p.lng, 0) / visible.length;
    return { lat, lng };
  }, [visible]);

  if (!isMapsKeyConfigured()) {
    return (
      <div className="ae-card p-8 text-center" data-testid="pipeline-map-no-key">
        <AlertTriangle className="h-8 w-8 mx-auto mb-3 text-amber-400" />
        <div className="text-sm text-slate-300">Google Maps API key not configured.</div>
        <div className="text-xs text-slate-500 mt-1">
          Set <code className="text-emerald-400">REACT_APP_GOOGLE_MAPS_API_KEY</code> in <code>frontend/.env</code>.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="pipeline-map">
      {/* Header + counts */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <MapPin className="h-3.5 w-3.5 text-emerald-400" />
          <span data-testid="pipeline-map-count" className="font-mono">
            {visible.length} <span className="text-slate-500">/ {points.length}</span> opportunities plotted
          </span>
          {missing > 0 && (
            <span className="text-amber-400 text-[10px] uppercase tracking-wider">
              · {missing} missing coords
            </span>
          )}
        </div>
      </div>

      {/* Stage filter chips */}
      <div className="flex flex-wrap gap-1.5" data-testid="pipeline-map-stage-filter">
        <FilterChip
          active={stageFilter === "all"} onClick={() => setStageFilter("all")}
          color="#10B981" label="All stages" count={points.length} testid="map-chip-all"
        />
        {Object.entries(STAGE_COLORS).map(([k, v]) => (
          counts[k] ? (
            <FilterChip
              key={k}
              active={stageFilter === k}
              onClick={() => setStageFilter(k)}
              color={v.bg}
              label={v.label}
              count={counts[k]}
              testid={`map-chip-${k}`}
            />
          ) : null
        ))}
      </div>

      {/* Map */}
      <div className="ae-card p-0 overflow-hidden" style={{ height: "560px" }}>
        <APIProvider apiKey={GMAPS_KEY} libraries={GMAPS_LIBS}>
          <Map
            mapId={GMAPS_MAP_ID}
            defaultCenter={center}
            defaultZoom={DEFAULT_ZOOM}
            gestureHandling="greedy"
            disableDefaultUI={false}
            mapTypeControl={false}
            streetViewControl={false}
            fullscreenControl={false}
            colorScheme="DARK"
            style={{ width: "100%", height: "100%" }}
            data-testid="pipeline-map-canvas"
          >
            {visible.map((p) => {
              const stage = p.cf_stage || "discovered";
              const c = STAGE_COLORS[stage] || STAGE_COLORS.discovered;
              return (
                <AdvancedMarker
                  key={p.oid}
                  position={{ lat: p.lat, lng: p.lng }}
                  onClick={() => setActive(p)}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50% 50% 50% 0",
                      transform: "rotate(-45deg)",
                      background: c.bg,
                      border: `2px solid ${c.border}`,
                      boxShadow: "0 2px 6px rgba(0,0,0,0.45)",
                      cursor: "pointer",
                    }}
                  />
                </AdvancedMarker>
              );
            })}
            {active && (
              <InfoWindow
                position={{ lat: active.lat, lng: active.lng }}
                onCloseClick={() => setActive(null)}
                pixelOffset={[0, -30]}
              >
                <div className="text-slate-900 text-xs min-w-[220px]" data-testid="pipeline-map-info">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">
                    {(STAGE_COLORS[active.cf_stage] || STAGE_COLORS.discovered).label}
                    {typeof active.cf_readiness_score === "number" && (
                      <span className="ml-2 font-mono text-emerald-700">{active.cf_readiness_score}</span>
                    )}
                  </div>
                  <div className="font-semibold mt-0.5 text-slate-900 leading-snug">{active.title}</div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    {active.city}, {active.state} · <span className="capitalize">{active.asset_type}</span>
                  </div>
                  {active.asking_price ? (
                    <div className="text-[11px] font-mono text-emerald-700 mt-0.5">
                      {fmtCurrencyFull(active.asking_price)}
                    </div>
                  ) : null}
                  {active.motivation_flag && (
                    <div className="text-[10px] uppercase tracking-wider text-amber-700 mt-1">
                      {active.motivation_flag}
                    </div>
                  )}
                  <Link
                    to={`/app/opportunities/${active.oid}`}
                    className="mt-2 inline-flex items-center gap-1 text-emerald-700 text-[11px] font-medium hover:underline"
                    data-testid="pipeline-map-info-link"
                  >
                    Open detail <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </div>
              </InfoWindow>
            )}
          </Map>
        </APIProvider>
      </div>
    </div>
  );
}


function FilterChip({ active, onClick, color, label, count, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-2 py-1 text-[11px] uppercase tracking-wider rounded-sm border transition-colors ${
        active
          ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-200"
          : "border-white/10 bg-white/0 text-slate-400 hover:border-white/20"
      }`}
    >
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      <span>{label}</span>
      <span className="font-mono text-slate-500">{count}</span>
    </button>
  );
}
